import { useContext, useState } from "react";
import { useNavigate } from "react-router";
import { I18nContext } from "../../../../../context/i18n.context";
import { DataI } from "../../../../../api/request/Lead/Interfaces/DataInterface.response";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { GlobalTypeFormPolicy } from "../../../../../utils/InternationlChanges";
import { DOMAIN } from "../../../../../constants/Global";
import { modificationsAvailable } from "../components/modifications/shared";
import { LanguageT } from "../../../../../context/GetLifeContext.context";

interface NewProfileI {
  userData?: DataI;
  navigateToRoute?: (route: string) => void;
}

// Inline toggle switch component
const ToggleSwitch = ({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    style={{
      position: "relative",
      width: 44,
      height: 24,
      borderRadius: 12,
      border: "none",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "background-color 0.2s",
      backgroundColor: disabled
        ? "#d1d5db"
        : checked
        ? "#4f46e5"
        : "#d1d5db",
      opacity: disabled ? 0.6 : 1,
      flexShrink: 0,
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 2,
        left: checked ? 22 : 2,
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: "#fff",
        transition: "left 0.2s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }}
    />
  </button>
);

const NewProfile = ({ userData, navigateToRoute }: NewProfileI) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const navigate = useNavigate();
  const lead = userData?.lead;

  const [commercialComms, setCommercialComms] = useState(false);
  const [smsAlerts, setSmsAlerts] = useState(true);

  // ES and AXA WL have the in-app "Modificaciones" section (signed requests +
  // direct contact changes); the other markets keep the external Typeform.
  const modifyData = () => {
    if (modificationsAvailable(DOMAIN) && navigateToRoute) {
      return navigateToRoute("modifications");
    }
    window.open(GlobalTypeFormPolicy[DOMAIN as LanguageT], "_blank");
  };

  const fullName = `${lead?.name ?? ""} ${lead?.lastName ?? ""} ${
    lead?.lastName2 ?? ""
  }`.trim();

  const cardStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    padding: 24,
  };

  const readonlyFieldStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    padding: "12px 16px",
  };

  const editableFieldStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
    borderBottom: "1px solid #f3f4f6",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 500,
    color: "#6b7280",
    marginBottom: 2,
  };

  const valueStyle: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 500,
    color: "#111827",
    wordBreak: "break-word" as const,
  };

  const sectionTitleStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    paddingBottom: 16,
    borderBottom: "1px solid #f3f4f6",
    marginBottom: 16,
  };

  const toggleRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 0",
    borderBottom: "1px solid #f3f4f6",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 720 }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>
          {translate("newDashboard.profile.title")}
        </h1>
        <p style={{ fontSize: 15, color: "#6b7280", marginTop: 4 }}>
          {translate("newDashboard.profile.subtitle")}
        </p>
      </div>

      {/* Card 1: Datos Personales */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>
          <GlobalIcon iconName="UserCircleIcon" size="XS" color="#4f46e5" />
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>
            {translate("newDashboard.profile.personalInfo")}
          </h2>
        </div>

        {/* Read-only fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 12 }}>
          <div>
            <p style={labelStyle}>{translate("newDashboard.profile.fullName")}</p>
            <div style={readonlyFieldStyle}>
              <GlobalIcon iconName="ShieldLockIcon" size="XS" color="#9ca3af" />
              <span style={valueStyle}>{fullName || "—"}</span>
            </div>
          </div>
          <div>
            <p style={labelStyle}>{translate("newDashboard.profile.dniNie")}</p>
            <div style={readonlyFieldStyle}>
              <GlobalIcon iconName="ShieldLockIcon" size="XS" color="#9ca3af" />
              <span style={valueStyle}>{lead?.idNumber || "—"}</span>
            </div>
          </div>
        </div>

        <p
          style={{
            fontSize: 13,
            color: "#9ca3af",
            fontStyle: "italic",
            margin: "0 0 16px",
            paddingBottom: 16,
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          {translate("newDashboard.profile.readonlyHint")}
        </p>

        {/* Editable fields */}
        <div style={editableFieldStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={labelStyle}>{translate("newDashboard.profile.email")}</p>
            <p style={valueStyle}>{lead?.email || "—"}</p>
          </div>
          <ButtonDS
            label={translate("newDashboard.profile.edit")}
            buttonType="tertiary"
            size="32"
            onClick={modifyData}
          />
        </div>

        <div style={editableFieldStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={labelStyle}>{translate("newDashboard.profile.phone")}</p>
            <p style={valueStyle}>{lead?.phone || "—"}</p>
          </div>
          <ButtonDS
            label={translate("newDashboard.profile.edit")}
            buttonType="tertiary"
            size="32"
            onClick={modifyData}
          />
        </div>

        <div style={{ ...editableFieldStyle, borderBottom: "none" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={labelStyle}>{translate("newDashboard.profile.address")}</p>
            <p style={valueStyle}>{lead?.addressLine || "—"}</p>
          </div>
          <ButtonDS
            label={translate("newDashboard.profile.edit")}
            buttonType="tertiary"
            size="32"
            onClick={modifyData}
          />
        </div>
      </div>

      {/* Card 2: Preferencias de Privacidad y Comunicación */}
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>
          <GlobalIcon iconName="MailIcon" size="XS" color="#4f46e5" />
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>
            {translate("newDashboard.profile.privacy")}
          </h2>
        </div>

        {/* Policy notifications — mandatory */}
        <div style={toggleRowStyle}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 }}>
                {translate("newDashboard.profile.policyNotifications")}
              </p>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: "#f3f4f6",
                  color: "#6b7280",
                  padding: "2px 8px",
                  borderRadius: 10,
                }}
              >
                {translate("newDashboard.profile.mandatory")}
              </span>
            </div>
            <p style={{ fontSize: 13, color: "#9ca3af", margin: "4px 0 0" }}>
              {translate("newDashboard.profile.policyNotificationsDesc")}
            </p>
          </div>
          <ToggleSwitch checked={true} onChange={() => {}} disabled={true} />
        </div>

        {/* Commercial communications */}
        <div style={toggleRowStyle}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 }}>
              {translate("newDashboard.profile.commercialComms")}
            </p>
            <p style={{ fontSize: 13, color: "#9ca3af", margin: "4px 0 0" }}>
              {translate("newDashboard.profile.commercialCommsDesc")}
            </p>
          </div>
          <ToggleSwitch checked={commercialComms} onChange={setCommercialComms} />
        </div>

        {/* SMS alerts */}
        <div style={{ ...toggleRowStyle, borderBottom: "none" }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 }}>
              {translate("newDashboard.profile.smsAlerts")}
            </p>
            <p style={{ fontSize: 13, color: "#9ca3af", margin: "4px 0 0" }}>
              {translate("newDashboard.profile.smsAlertsDesc")}
            </p>
          </div>
          <ToggleSwitch checked={smsAlerts} onChange={setSmsAlerts} />
        </div>
      </div>

      {/* Card 3: Seguridad — hidden until change-password + 2FA flows are ready */}
      {/*
      <div style={cardStyle}>
        <div style={sectionTitleStyle}>
          <GlobalIcon iconName="ShieldLockIcon" size="XS" color="#4f46e5" />
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>
            {translate("newDashboard.profile.security")}
          </h2>
        </div>

        <div style={{ ...toggleRowStyle, borderBottom: "1px solid #f3f4f6" }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 }}>
              {translate("newDashboard.profile.changePassword")}
            </p>
          </div>
          <ButtonDS
            label={translate("newDashboard.profile.changePassword")}
            buttonType="secondary"
            size="32"
            onClick={() => navigate("/reset")}
          />
        </div>

        <div style={{ ...toggleRowStyle, borderBottom: "none" }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: "#111827", margin: 0 }}>
                {translate("newDashboard.profile.twoFactor")}
              </p>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: "#fef3c7",
                  color: "#92400e",
                  padding: "2px 8px",
                  borderRadius: 10,
                }}
              >
                {translate("newDashboard.profile.comingSoon")}
              </span>
            </div>
          </div>
          <ToggleSwitch checked={false} onChange={() => {}} disabled={true} />
        </div>
      </div>
      */}
    </div>
  );
};

export default NewProfile;
